/**
 * Custom hooks for fetching dispatcher options for filters
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { MultiSelectOption } from '../components/MultiSelect';
import ordersService from '../services/orders.service';

interface DispatcherOptionsResult {
  options: MultiSelectOption[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching order dispatcher options
 * Uses /orders/dispatchers endpoint
 * Includes individual dispatchers and dispatcher groups
 */
export const useOrderDispatcherOptions = (): DispatcherOptionsResult => {
  const [options, setOptions] = useState<MultiSelectOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchOptions = useCallback(async () => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      setError(null);
      const response = await ordersService.getOrderDispatchers(controller.signal);

      // Individual dispatchers
      const dispatcherOptions = response.data.map((d: { username: string; displayname: string }) => ({
        value: d.username,
        label: d.displayname,
      }));

      // Dispatcher groups (at the top, after "All" option)
      const groupOptions = (response.groups || []).map((g: { groupname: string; dispatcherlist: string }) => ({
        value: g.dispatcherlist,
        label: g.groupname,
      }));

      // Headers
      const groupsHeader = { value: '__groups_header__', label: 'Groups', isHeader: true };
      const dispatchersHeader = { value: '__dispatchers_header__', label: 'Dispatchers', isHeader: true };

      setOptions(groupOptions.length > 0
        ? [groupsHeader, ...groupOptions, dispatchersHeader, ...dispatcherOptions]
        : dispatcherOptions
      );
    } catch (err: any) {
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch order dispatcher options:', err);
      setError('Failed to load order dispatchers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOptions();
    return () => abortControllerRef.current?.abort();
  }, [fetchOptions]);

  return { options, loading, error, refetch: fetchOptions };
};

/**
 * Hook for fetching driver dispatcher options
 * Uses /drivers/dispatchers endpoint
 */
export const useDriverDispatcherOptions = (): DispatcherOptionsResult => {
  const [options, setOptions] = useState<MultiSelectOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchOptions = useCallback(async () => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      setError(null);
      const response = await ordersService.getDriverDispatchers(controller.signal);

      const dispatcherOptions = response.data.map((d: { username: string; displayname: string }) => ({
        value: d.username,
        label: d.displayname,
      }));

      const groupOptions = (response.groups || []).map((g: { groupname: string; dispatcherlist: string }) => ({
        value: g.dispatcherlist,
        label: g.groupname,
      }));

      const groupsHeader = { value: '__groups_header__', label: 'Groups', isHeader: true };
      const dispatchersHeader = { value: '__dispatchers_header__', label: 'Dispatchers', isHeader: true };

      setOptions(groupOptions.length > 0
        ? [groupsHeader, ...groupOptions, dispatchersHeader, ...dispatcherOptions]
        : dispatcherOptions
      );
    } catch (err: any) {
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch driver dispatcher options:', err);
      setError('Failed to load driver dispatchers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOptions();
    return () => abortControllerRef.current?.abort();
  }, [fetchOptions]);

  return { options, loading, error, refetch: fetchOptions };
};
